import {
  DEFAULT_LOAD_TIMEOUT_MS,
  DEFAULT_POLL_INTERVAL_MS,
  DEFAULT_SCRIPT_ID,
  DEFAULT_SCRIPT_SRC,
} from './constants'
import { emitRuntimeEvent } from './events'
import { ensureScript } from './script'
import { getRuntimeState, resolveApiIfReady } from './state'
import type {
  BrowserWindow,
  TurnstileApi,
  TurnstileRuntimeOptions,
} from './types'

/**
 * Loads the Cloudflare Turnstile browser API once and resolves when it is ready.
 */
export const loadTurnstile = async (options?: TurnstileRuntimeOptions): Promise<TurnstileApi> => {
  const readyApi = resolveApiIfReady()
  if (readyApi) return readyApi

  if (typeof window === 'undefined') {
    throw new Error('Turnstile can only be loaded in a browser environment')
  }

  const browserWindow = window as BrowserWindow
  const state = getRuntimeState(browserWindow)
  if (state.loadPromise) return state.loadPromise

  const scriptId = options?.scriptId ?? DEFAULT_SCRIPT_ID
  const scriptSrc = options?.scriptSrc ?? DEFAULT_SCRIPT_SRC
  const loadTimeoutMs = options?.loadTimeoutMs ?? DEFAULT_LOAD_TIMEOUT_MS
  const pollIntervalMs = options?.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS

  state.loadPromise = new Promise<TurnstileApi>((resolve, reject) => {
    const script = ensureScript(scriptId, scriptSrc)
    const expectedSrc = new URL(scriptSrc, window.location.origin).toString()
    if (script.src !== expectedSrc) {
      emitRuntimeEvent(options, 'warn', 'turnstile.loader_script_src_mismatch', {
        expectedSrc,
        actualSrc: script.src,
      })
    }

    let settled = false
    const startedAt = Date.now()
    const finalize = (handler: () => void) => {
      if (settled) return
      settled = true
      script.removeEventListener('error', handleError)
      script.removeEventListener('load', handleLoad)
      window.clearInterval(pollId)
      window.clearTimeout(timeoutId)
      handler()
    }

    const tryResolve = () => {
      const api = browserWindow.turnstile
      if (!api) return
      finalize(() => {
        emitRuntimeEvent(options, 'info', 'turnstile.loader_ready', {
          elapsedMs: Date.now() - startedAt,
        })
        resolve(api)
      })
    }

    const handleError = () => {
      finalize(() => {
        state.loadPromise = null
        reject(new Error('Cloudflare Turnstile script failed to load'))
      })
    }

    const handleLoad = () => {
      tryResolve()
    }

    script.addEventListener('error', handleError)
    script.addEventListener('load', handleLoad)

    const pollId = window.setInterval(tryResolve, pollIntervalMs)
    const timeoutId = window.setTimeout(() => {
      finalize(() => {
        state.loadPromise = null
        reject(new Error(`Cloudflare Turnstile did not become ready within ${loadTimeoutMs}ms`))
      })
    }, loadTimeoutMs)

    tryResolve()
  })

  return state.loadPromise
}
